<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { useAuthStore } from '~/auth/stores/auth.store';
import { useCommonStore } from '~/common/stores/common.store.js';

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (value) => ['teams', 'roles'].includes(value),
  },
  asset_id: {
    type: String,
    default: null,
  },
  selectedAssets: {
    type: Array,
  },
  updateFormData:{
    type: Function
  },
  options: {
    type: Object,
  },
  tag_class: {
    type: String,
    default: 'max-w-[200px]',
  },
  multi: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['clear', 'open', 'change']);

const route = useRoute();
const common_store = useCommonStore();
const auth_store = useAuthStore();
const tagsElement = ref(null);
const entity_data = computed(() => {
  const asset_id = props?.asset_id ? props.asset_id : route.params.asset_id;
  const uniqueEntityIds = new Set(); // To track unique entity IDs
  const result = [];
  const isTeams = props.type === 'teams';
  const existingFilterKey = isTeams ? 'existing_teams' : 'existing_roles';

  // If selectedAssets are provided, organize entities in a hierarchical structure
  if (props.selectedAssets && props.selectedAssets.length) {
    // Get organization level entities (null asset_id)
    const orgEntities = isTeams
      ? common_store.scope_teams(null) || []
      : common_store.scope_roles(null) || [];

    // Create organization group if there are org entities
    if (orgEntities.length > 0) {
      let orgEntitiesFormatted = orgEntities.map((entity) => {
        uniqueEntityIds.add(entity.uid);
        return {
          name: entity.name,
          uid: entity.uid,
        };
      });

      // Filter out existing entities if needed
      if (props?.options?.[existingFilterKey]) {
        orgEntitiesFormatted = orgEntitiesFormatted.filter(
          item => !props.options[existingFilterKey].includes(item.uid),
        );
      }

      // Add organization group
      if (orgEntitiesFormatted.length > 0) {
        result.push({
          label: common_store.get_organization(auth_store.current_organization_uid)?.name || 'Organization',
          items: orgEntitiesFormatted,
        });
      }
    }

    // Add entities from each selected asset as separate groups
    props.selectedAssets.forEach((assetId) => {
      const assetEntities = isTeams
        ? common_store.scope_teams(assetId) || []
        : common_store.scope_roles(assetId) || [];

      // Only create a group if the asset has entities
      if (assetEntities.length > 0) {
        let assetEntitiesFormatted = assetEntities
          .filter(entity => !uniqueEntityIds.has(entity.uid)) // Filter out duplicates
          .map((entity) => {
            uniqueEntityIds.add(entity.uid);
            return {
              name: entity.name,
              uid: entity.uid,
            };
          });

        // Filter out existing entities if needed
        if (props?.options?.[existingFilterKey]) {
          assetEntitiesFormatted = assetEntitiesFormatted.filter(
            item => !props.options[existingFilterKey].includes(item.uid),
          );
        }

        // Add asset group if it has entities
        if (assetEntitiesFormatted.length > 0) {
          const asset = common_store.get_asset(assetId);
          result.push({
            label: asset?.name || `Asset ${assetId}`,
            items: assetEntitiesFormatted,
          });
        }
      }
    });

    return result;
  }
  else {
    // Original behavior - get entities for a single asset
    let entities = isTeams
      ? common_store.scope_teams(asset_id) || []
      : common_store.scope_roles(asset_id) || [];

    // Map entities to the required format
    entities = entities.map((entity) => {
      return {
        name: entity.name,
        uid: entity.uid,
      };
    });

    // Filter out existing entities if needed
    if (props?.options?.[existingFilterKey])
      entities = entities.filter(item => !props.options[existingFilterKey].includes(item.uid));

    return entities;
  }
});
watch(
  () => props.selectedAssets,
  (newValue, oldValue) => {
    if (!newValue || !oldValue) return;

    // Find removed asset
    const removedAsset = oldValue.find(asset => !newValue.includes(asset));
    if (!removedAsset) return;

    // Get entities for the removed asset
    const removedAssetEntities = props.type === 'teams'
      ? common_store.scope_teams(removedAsset)?.map(team => team.uid) || []
      : common_store.scope_roles(removedAsset)?.map(role => role.uid) || [];

    // Get current selection from template
    const currentSelection = tagsElement.value?.value || [];
    if (!Array.isArray(currentSelection)) return;

    // Remove entities that belong to the removed asset
    const updatedSelection = currentSelection.filter(selectedId =>
      !removedAssetEntities.includes(selectedId)
    );
    props.updateFormData({[props.type]:updatedSelection})
    // Emit the filtered selection
    // emit('change', updatedSelection);
  },
  { deep: true }
);
</script>

<template>
  <TagsElement
    ref="tagsElement"
    v-if="multi"
    v-bind="{
      'autocomplete': 'off',
      'search': true,
      'close-on-select': false,
      'groups': selectedAssets && selectedAssets.length > 0,
      'group-hide-empty': selectedAssets && selectedAssets.length > 0,
      'add-classes': {
        TagsElement: {
          select: {
            groupLabel: 'font-semibold text-gray-700 bg-gray-50 px-2 py-1',
          },
        },
      },
      'object': true,
      'group-select': selectedAssets && selectedAssets.length > 0,
      'label-prop': 'name',
      'value-prop': 'uid',
      'track-by': 'name',
      'items': entity_data,
      ...options,
    }"
    @clear="emit('clear', $event)"
    @open="emit('open', $event)"
    @change="emit('change', $event)"
  >
    <template #group-label="{ group }">
      <div class="flex items-center" :class="{ 'text-primary-500': group.label === (common_store.get_organization(auth_store.current_organization_uid)?.name || 'Organization') }">
        {{ group.label }}
      </div>
    </template>
    <template #option="{ option, group }">
      <div :class="{ 'pl-4': group && !option.label }">
        <HawkMembers v-if="type === 'teams'" :members="option.uid" type="label" size="xs" :has_avatar="false" />
        <HawkRoleName v-else :uid="option.uid" />
      </div>
    </template>
    <template #tag="{ option, handleTagRemove }">
      <template v-if="type === 'teams'">
        <div
          v-if="option.default"
          v-tippy="{ content: 'Marked as default' }"
          class="bg-gray-100 !text-gray-500 flex items-center whitespace-nowrap text-sm rounded-lg border py-0.5 px-1.5 mr-1 mb-1"
        >
          <HawkMembers :members="option" name_class="!font-normal" type="label" size="xs" :has_avatar="false" name_classes="!text-gray-500" />
        </div>
        <div
          v-else
          class="flex items-center whitespace-nowrap text-sm rounded-lg border py-0.5 px-1.5 mr-1 mb-1"
        >
          <HawkMembers :members="option" name_class="!font-normal" type="label" size="xs" :has_avatar="false" />
          <div class="hover:bg-gray-100 rounded ml-1">
            <IconHawkXClose class="text-gray-400 w-4 h-4" @mousedown.prevent="handleTagRemove(option, $event)" />
          </div>
        </div>
      </template>
      <template v-else>
        <div class="flex items-center whitespace-nowrap text-sm rounded-lg border py-0.5 px-1.5 mr-1 mb-1">
          <HawkRoleName :uid="option?.uid ? option.uid : option" class="text-gray-700 font-medium" />
          <div class="hover:bg-gray-100 rounded ml-1">
            <IconHawkXClose class="text-gray-400 w-4 h-4" @mousedown.prevent="handleTagRemove(option, $event)" />
          </div>
        </div>
      </template>
    </template>
    <template v-if="$slots.label" #label>
      <slot name="label" />
    </template>
  </TagsElement>
  <SelectElement
    v-else
    v-bind="{
      'autocomplete': 'off',
      'search': true,
      'close-on-select': true,
      'groups': true,
      'group-hide-empty': true,
      'hide-selected': true,
      'object': true,
      'label-prop': 'name',
      'value-prop': 'uid',
      'track-by': 'name',
      'items': entity_data,
      ...options,
    }"
    @open="emit('open', $event)"
    @change="emit('change', $event)"
  >
    <template #option="{ option }">
      <p class="text-gray-700">
        {{ option.name }}
      </p>
    </template>
    <template #single-label="{ value }">
      <div class="flex items-center h-full absolute left-0 pl-3">
        {{ value.name }}
      </div>
    </template>
  </SelectElement>
</template>
